import { useEffect, useState } from "react";
import { Link } from "react-router";

import { PATHS } from "@/app/routes/paths";
import { beginSso } from "@/core/sso/irwms";
import { AuthLayout } from "@/features/auth/AuthLayout";

/**
 * Rendered in place of a protected page when there is no WMS session and IRWMS
 * SSO is configured: hands the browser to the identity provider at once. If the
 * person already signed in on the portal (or any other workshop application)
 * Keycloak answers without a prompt and they land back on `returnTo`.
 */
export function SsoRedirect({ returnTo }: { returnTo: string }) {
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    beginSso(returnTo).catch((err: unknown) => {
      setFailed(err instanceof Error ? err.message : "Could not reach the sign-on service");
    });
  }, [returnTo]);

  return (
    <AuthLayout>
      <div className="py-6 text-center" role="status" aria-live="polite">
        {failed ? (
          <>
            <p className="font-semibold" style={{ color: "var(--color-error)" }}>
              Sign-on service unavailable
            </p>
            <p className="mt-2 text-sm" style={{ color: "var(--color-text-secondary)" }}>
              {failed}
            </p>
            <Link
              to={`${PATHS.auth.login}?local=1`}
              className="mt-4 inline-block text-sm font-medium hover:underline"
              style={{ color: "var(--color-brand-primary)" }}
            >
              Sign in with PF number and password instead
            </Link>
          </>
        ) : (
          <>
            <span
              className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-t-transparent"
              style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }}
              aria-hidden="true"
            />
            <p className="mt-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
              Taking you to the IRWMS sign-in…
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
